/**
 * Collection Filters JavaScript
 * AJAX-based filtering and sorting for collection pages
 * Feature: 020-collection-filtering-sorting
 */

class CollectionFilters {
  constructor(container) {
    this.container = container;
    // Get ALL forms (mobile + desktop) for event binding
    this.forms = container.querySelectorAll('[data-filter-form]');
    this.sectionId = container.closest('[data-section-id]')?.dataset.sectionId;
    this.announcement = container.querySelector('[data-filter-announcement]');
    this.debounceTimer = null;

    if (!this.forms.length) return;
    this.init();
  }

  init() {
    this.bindEvents();
  }

  // Get the currently visible/active form
  getActiveForm() {
    // Desktop form is inside .collection-filters__desktop
    const desktopContainer = this.container.querySelector('.collection-filters__desktop');
    const mobileContainer = this.container.querySelector('.collection-filters__accordion--mobile');

    // Check which container is visible (display !== none)
    if (desktopContainer && getComputedStyle(desktopContainer).display !== 'none') {
      return desktopContainer.querySelector('[data-filter-form]');
    }
    if (mobileContainer && getComputedStyle(mobileContainer).display !== 'none') {
      return mobileContainer.querySelector('[data-filter-form]');
    }

    // Fallback to first form
    return this.forms[0];
  }

  bindEvents() {
    // Bind events to ALL forms (both mobile and desktop)
    this.forms.forEach(form => {
      // Intercept form submission
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.applyFilters();
      });

      // Auto-apply on sort change
      const sortSelect = form.querySelector('[data-sort-select]');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => this.applyFilters());
      }

      // Auto-apply on checkbox change
      form.querySelectorAll('[data-filter-checkbox]').forEach(checkbox => {
        checkbox.addEventListener('change', () => this.applyFilters());
      });

      // Auto-apply on select change
      form.querySelectorAll('[data-filter-select]').forEach(select => {
        select.addEventListener('change', () => this.applyFilters());
      });

      // Debounced price input handling
      form.querySelectorAll('[data-price-input]').forEach(input => {
        input.addEventListener('input', () => this.handlePriceInput());
      });
    });

    // Handle filter chip removal (AJAX)
    this.container.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-filter-remove]');
      if (removeBtn) {
        e.preventDefault();
        this.removeFilter(removeBtn.href);
      }

      const clearAllBtn = e.target.closest('[data-filter-clear-all]');
      if (clearAllBtn) {
        e.preventDefault();
        this.clearAllFilters(clearAllBtn.href);
      }

      const resetBtn = e.target.closest('[data-filter-reset]');
      if (resetBtn) {
        e.preventDefault();
        this.clearAllFilters(resetBtn.href);
      }
    });

    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.handlePopState();
    });
  }

  handlePriceInput() {
    // Debounce price input to avoid excessive API calls
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (this.validatePriceRange()) {
        this.applyFilters();
      }
    }, 500);
  }

  validatePriceRange() {
    const form = this.getActiveForm();
    const minInput = form.querySelector('[data-price-input="min"]');
    const maxInput = form.querySelector('[data-price-input="max"]');

    if (minInput && maxInput) {
      const min = parseFloat(minInput.value) || 0;
      const max = parseFloat(maxInput.value) || Infinity;

      if (min > max && maxInput.value !== '') {
        // Min exceeds max - don't apply
        return false;
      }
    }
    return true;
  }

  async applyFilters() {
    const form = this.getActiveForm();
    const formData = new FormData(form);
    const params = new URLSearchParams();

    // Process form data
    for (const [key, value] of formData.entries()) {
      if (value === '' || value === null) continue;

      // Price values are passed as-is (Shopify expects currency units, not cents)
      // e.g., filter.v.price.lte=100 means £100, not 100 cents
      if (key.includes('.price.')) {
        const price = parseFloat(value);
        if (!isNaN(price) && price >= 0) {
          params.append(key, price.toString());
        }
      } else {
        params.append(key, value);
      }
    }

    // Handle select dropdowns (value format: "param_name=value")
    form.querySelectorAll('[data-filter-select]').forEach(select => {
      const selectedValue = select.value;
      if (selectedValue && selectedValue.includes('=')) {
        const [paramName, paramValue] = selectedValue.split('=');
        if (paramName && paramValue) {
          params.set(paramName, decodeURIComponent(paramValue));
        }
      }
    });

    await this.fetchAndUpdate(params);
  }

  async removeFilter(url) {
    const urlObj = new URL(url, window.location.origin);
    const params = urlObj.searchParams;
    await this.fetchAndUpdate(params);
  }

  async clearAllFilters(url) {
    const urlObj = new URL(url, window.location.origin);
    const params = urlObj.searchParams;
    await this.fetchAndUpdate(params);
  }

  async fetchAndUpdate(params) {
    // Add section_id for partial render
    if (this.sectionId) {
      params.set('section_id', this.sectionId);
    }

    const fetchUrl = `${window.location.pathname}?${params.toString()}`;

    try {
      this.setLoadingState(true);

      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      this.updateDOM(html);
      this.updateURL(params);
      this.announceResults();

    } catch (error) {
      console.error('Filter error:', error);
      // Fallback: submit form for full page reload
      this.getActiveForm().submit();
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(loading) {
    this.container.classList.toggle('collection-filters--loading', loading);
    // Disable inputs on ALL forms
    this.forms.forEach(form => {
      form.querySelectorAll('input, select, button').forEach(el => {
        el.disabled = loading;
      });
    });
  }

  /**
   * Safely replace element content using DOM methods instead of innerHTML
   * This prevents XSS by using the browser's DOM parser which handles sanitization
   * @param {HTMLElement} target - Element to update
   * @param {HTMLElement} source - Source element with new content
   */
  safeReplaceContent(target, source) {
    // Clear existing content
    while (target.firstChild) {
      target.removeChild(target.firstChild);
    }
    // Move nodes from source to target (no string parsing)
    while (source.firstChild) {
      target.appendChild(source.firstChild);
    }
  }

  /**
   * Safely replace an element with another element
   * @param {HTMLElement} oldEl - Element to replace
   * @param {HTMLElement} newEl - New element
   */
  safeReplaceElement(oldEl, newEl) {
    const parent = oldEl.parentNode;
    if (parent) {
      // Clone the new element to ensure it's in the document
      const cloned = newEl.cloneNode(true);
      parent.replaceChild(cloned, oldEl);
      return cloned;
    }
    return null;
  }

  updateDOM(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Update the entire products container (handles both grid and empty states)
    // Using safe DOM replacement instead of innerHTML
    const newProductsContainer = doc.querySelector('.collection-listing__products');
    const currentProductsContainer = document.querySelector('.collection-listing__products');

    if (newProductsContainer && currentProductsContainer) {
      this.safeReplaceContent(currentProductsContainer, newProductsContainer);
    }

    // Update product count using textContent (safe)
    const newCount = doc.querySelector('[data-product-count]');
    const currentCount = document.querySelector('[data-product-count]');
    if (newCount && currentCount) {
      currentCount.textContent = newCount.textContent;
    }

    // Update active filters using safe DOM methods
    const newActiveFilters = doc.querySelector('[data-active-filters]');
    const currentActiveFilters = this.container.querySelector('[data-active-filters]');

    if (newActiveFilters) {
      if (currentActiveFilters) {
        this.safeReplaceElement(currentActiveFilters, newActiveFilters);
      } else {
        // Insert active filters after form using appendChild
        const form = this.container.querySelector('[data-filter-form]');
        if (form && form.parentNode) {
          const cloned = newActiveFilters.cloneNode(true);
          form.parentNode.insertBefore(cloned, form.nextSibling);
        }
      }
    } else if (currentActiveFilters) {
      currentActiveFilters.remove();
    }

    // Update filter forms (for checkbox states, counts, etc.)
    // Both mobile and desktop forms need updating
    const newForms = doc.querySelectorAll('[data-filter-form]');
    this.forms.forEach((currentForm, formIndex) => {
      const newForm = newForms[formIndex];
      if (newForm && currentForm) {
        // Update filter groups while preserving form structure
        const newFilterGroups = newForm.querySelectorAll('[data-filter-group]');
        const currentFilterGroups = currentForm.querySelectorAll('[data-filter-group]');

        newFilterGroups.forEach((newGroup, index) => {
          if (currentFilterGroups[index]) {
            // Check if this is a price range filter - don't replace it to preserve user input
            const isPriceRange = currentFilterGroups[index].querySelector('[data-price-range]');
            if (!isPriceRange) {
              this.safeReplaceContent(currentFilterGroups[index], newGroup);
            }
            // For price range, we keep the user's input values as-is
          }
        });
      }
    });

    // Update reset button visibility in both forms
    const newResetWrappers = doc.querySelectorAll('.collection-filters__reset-wrapper');
    this.forms.forEach((currentForm, formIndex) => {
      const currentResetWrapper = currentForm.querySelector('.collection-filters__reset-wrapper');
      const newResetWrapper = newResetWrappers[formIndex];

      if (newResetWrapper && !currentResetWrapper) {
        // Add reset button if it doesn't exist using safe DOM methods
        const noscriptEl = currentForm.querySelector('noscript');
        if (noscriptEl && noscriptEl.parentNode) {
          const cloned = newResetWrapper.cloneNode(true);
          noscriptEl.parentNode.insertBefore(cloned, noscriptEl);
        }
      } else if (!newResetWrapper && currentResetWrapper) {
        // Remove reset button if no longer needed
        currentResetWrapper.remove();
      } else if (newResetWrapper && currentResetWrapper) {
        // Update existing reset button using safe replacement
        this.safeReplaceElement(currentResetWrapper, newResetWrapper);
      }
    });

    // Re-bind events for new elements
    this.bindFilterEvents();
  }

  bindFilterEvents() {
    // Re-bind events on ALL forms after DOM update
    this.forms.forEach(form => {
      // Re-bind checkbox events
      form.querySelectorAll('[data-filter-checkbox]').forEach(checkbox => {
        checkbox.addEventListener('change', () => this.applyFilters());
      });

      // Re-bind select events
      form.querySelectorAll('[data-filter-select]').forEach(select => {
        select.addEventListener('change', () => this.applyFilters());
      });

      // Re-bind price input events
      form.querySelectorAll('[data-price-input]').forEach(input => {
        input.addEventListener('input', () => this.handlePriceInput());
      });
    });
  }

  updateURL(params) {
    // Remove section_id from URL (it's only for AJAX)
    params.delete('section_id');

    // Reset page to 1 when filters change
    params.delete('page');

    // Clean empty params
    const cleanParams = new URLSearchParams();
    for (const [key, value] of params.entries()) {
      if (value !== '' && value !== null) {
        cleanParams.append(key, value);
      }
    }

    const queryString = cleanParams.toString();
    const url = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    window.history.replaceState({ filters: true }, '', url);
  }

  handlePopState() {
    // Reload page on back/forward to restore filter state
    window.location.reload();
  }

  announceResults() {
    if (this.announcement) {
      const count = document.querySelector('[data-product-count]')?.textContent || '';
      this.announcement.textContent = count;
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('[data-collection-filters]');
  if (container) {
    new CollectionFilters(container);
  }
});
